import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, X, Trash2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useScheduledPosts, useCreateScheduledPost, useCancelScheduledPost } from "@/hooks/use-schedule";
import { useProfiles } from "@/hooks/use-profiles";

export const Route = createFileRoute("/_auth/schedule")({
  component: SchedulePage,
});

const NETWORK_LABEL: Record<string, string> = {
  x: "X / Twitter", ig: "Instagram", fb: "Facebook", li: "LinkedIn",
  yt: "YouTube", tt: "TikTok", google: "Google Business",
};

function SchedulePage() {
  const posts = useScheduledPosts({ limit: 100 });
  const cancel = useCancelScheduledPost();
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink-900 dark:text-white">Programar publicaciones</h1>
          <p className="text-sm text-ink-500 mt-1">Cola de contenido por red social.</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Plus size={16} /> Nueva publicación
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Cola de publicaciones</CardTitle>
          <CardDescription>{posts.data ? `${posts.data.length} programadas` : "Cargando…"}</CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-ink-100 dark:divide-ink-800">
          {posts.isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 mb-2" />)
          ) : !posts.data || posts.data.length === 0 ? (
            <p className="text-sm text-ink-500 py-6 text-center">Sin publicaciones programadas.</p>
          ) : (
            posts.data.map((p) => {
              const nets = (p.networks as { network: string }[]) || [];
              return (
                <div key={p.id} className="py-3 flex items-start gap-3">
                  <Badge variant={p.status === "publicado" ? "good" : p.status === "fallido" ? "bad" : p.status === "cancelado" ? "muted" : "warn"}>
                    {p.status}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-ink-700 dark:text-ink-200 line-clamp-2">{p.text || "(sin texto)"}</p>
                    <p className="text-[11px] text-ink-500 mt-1">
                      {new Date(p.scheduled_at).toLocaleString("es-PE")}
                      {nets.length > 0 && <> · {nets.map(n => NETWORK_LABEL[n.network] ?? n.network).join(", ")}</>}
                    </p>
                  </div>
                  {p.status === "programado" && (
                    <button
                      onClick={() => {
                        if (confirm("¿Cancelar esta publicación?")) cancel.mutate(p.id);
                      }}
                      className="text-ink-400 hover:text-bad p-2"
                      title="Cancelar"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {showModal && <NewPostModal onClose={() => setShowModal(false)} />}
    </div>
  );
}

const NETWORKS = [
  { id: "x", label: "X / Twitter" },
  { id: "ig", label: "Instagram" },
  { id: "fb", label: "Facebook" },
  { id: "li", label: "LinkedIn" },
  { id: "yt", label: "YouTube" },
  { id: "tt", label: "TikTok" },
];

const postSchema = z.object({
  text: z.string().min(1, "Texto obligatorio").max(5000),
  scheduled_at: z.string().min(1, "Fecha obligatoria"),
  first_comment: z.string().optional(),
  location: z.string().optional(),
});
type PostInput = z.infer<typeof postSchema>;

function NewPostModal({ onClose }: { onClose: () => void }) {
  const create = useCreateScheduledPost();
  const profiles = useProfiles();
  const [selectedNets, setSelectedNets] = useState<string[]>([]);

  // Default a 1h en el futuro, formato datetime-local
  const defaultDt = new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16);

  const form = useForm<PostInput>({
    resolver: zodResolver(postSchema),
    defaultValues: { scheduled_at: defaultDt },
  });

  const text = form.watch("text") || "";
  const xLen = text.length;
  const xLimit = 280;
  const igLimit = 2200;

  function toggleNet(id: string) {
    setSelectedNets((prev) => (prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]));
  }

  async function onSubmit(data: PostInput) {
    if (selectedNets.length === 0) {
      alert("Selecciona al menos una red social");
      return;
    }
    try {
      await create.mutateAsync({
        ...data,
        networks: selectedNets.map((n) => ({ network: n })),
        scheduled_at: new Date(data.scheduled_at).toISOString(),
        status: "programado",
      });
      onClose();
    } catch {}
  }

  return (
    <div className="fixed inset-0 bg-ink-950/60 z-50 flex items-center justify-center p-4 overflow-auto">
      <Card className="w-full max-w-2xl my-8">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Nueva publicación</CardTitle>
            <CardDescription>Programa contenido para una o varias redes.</CardDescription>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700">
            <X size={20} />
          </button>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label>Redes sociales</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {NETWORKS.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => toggleNet(n.id)}
                    className={`px-3 h-8 rounded-md text-xs font-medium border transition ${
                      selectedNets.includes(n.id)
                        ? "bg-brand-600 text-white border-brand-600"
                        : "border-ink-200 dark:border-ink-700 text-ink-700 dark:text-ink-300 hover:border-brand-500"
                    }`}
                  >
                    {n.label}
                  </button>
                ))}
              </div>
              {profiles.data && profiles.data.length === 0 && (
                <p className="text-xs text-warn mt-2">
                  ⚠️ No tienes perfiles sociales conectados. Conéctalos en Configuración primero.
                </p>
              )}
            </div>

            <div>
              <Label>Texto del post</Label>
              <textarea
                className="mt-1 w-full min-h-[120px] rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 py-2 text-sm"
                placeholder="¿Qué quieres compartir hoy?"
                {...form.register("text")}
              />
              <div className="flex justify-between text-[11px] mt-1">
                {form.formState.errors.text && (
                  <span className="text-bad">{form.formState.errors.text.message}</span>
                )}
                <div className="ml-auto flex gap-3 text-ink-500">
                  <span className={selectedNets.includes("x") && xLen > xLimit ? "text-bad" : ""}>
                    X: {xLen}/{xLimit}
                  </span>
                  <span className={selectedNets.includes("ig") && xLen > igLimit ? "text-bad" : ""}>
                    IG: {xLen}/{igLimit}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fecha y hora</Label>
                <Input
                  type="datetime-local"
                  className="mt-1"
                  {...form.register("scheduled_at")}
                />
              </div>
              <div>
                <Label>Ubicación (opcional)</Label>
                <Input className="mt-1" placeholder="Lima, Perú" {...form.register("location")} />
              </div>
            </div>

            <div>
              <Label>Primer comentario (opcional)</Label>
              <Input
                className="mt-1"
                placeholder="Hashtags para IG, link bio, etc."
                {...form.register("first_comment")}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" disabled={create.isPending || selectedNets.length === 0}>
                {create.isPending ? "Programando…" : "Programar publicación"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
